/**
 * AudioDownloadService — orchestrates surah / hizb / full-Quran downloads.
 *
 * Technically downloads ayah-by-ayah (so the player can highlight verses
 * during playback), but the public API only exposes scope-level methods.
 *
 * Progress is broadcast via a tiny pub/sub so multiple UI surfaces can
 * subscribe (popup, manager page, mini-player).
 */
import { audioStorage } from './audioStorage';
import { quranAudioProvider } from './quranAudioProvider';
import {
  getAyahsForSurah,
  getAyahsForHizb,
  getAyahsForFullQuran,
} from './quranScopes';

const subscribers = new Map();      // downloadId -> Set<callback>
const inflight   = new Map();       // downloadId -> AbortController
const progresses = new Map();       // downloadId -> DownloadProgress

const AVERAGE_AYAH_BYTES_128 = 90 * 1024; // ~90 KB at 128 kbps

function newDownloadId() {
  return `dl_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function publish(downloadId) {
  const subs = subscribers.get(downloadId);
  const p = progresses.get(downloadId);
  if (subs && p) subs.forEach((cb) => { try { cb(p); } catch {} });
}

function setProgress(downloadId, patch) {
  const cur = progresses.get(downloadId) || {};
  const next = { ...cur, ...patch };
  next.progressPercent = next.totalFiles
    ? Math.round(((next.downloadedFiles || 0) / next.totalFiles) * 100)
    : 0;
  progresses.set(downloadId, next);
  publish(downloadId);
}

async function fetchAyahBlob(remoteUrl, signal) {
  // everyayah.com sends Access-Control-Allow-Origin:* on audio responses,
  // so a regular CORS fetch + .blob() works in the browser.
  const r = await fetch(remoteUrl, {
    signal,
    mode: 'cors',
    cache: 'no-store',
    referrerPolicy: 'no-referrer',
    credentials: 'omit',
  });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.blob();
}

async function ensureMissingFiles(ayahs, reciterId, riwaya) {
  const missing = [];
  for (const a of ayahs) {
    const present = await audioStorage.isAyahDownloaded(a.ayahKey, reciterId, riwaya);
    if (!present) missing.push(a);
  }
  return missing;
}

async function downloadAyahsToCache(ayahs, reciterId, riwaya, downloadId) {
  const reciter = quranAudioProvider.getReciterById(reciterId);
  const ctrl = new AbortController();
  inflight.set(downloadId, ctrl);

  let downloaded = 0;
  let failed = 0;
  for (const a of ayahs) {
    if (ctrl.signal.aborted) {
      setProgress(downloadId, { status: 'cancelled' });
      return { downloaded, failed, cancelled: true };
    }
    setProgress(downloadId, { currentAyahKey: a.ayahKey });
    const remoteUrl = quranAudioProvider.buildAyahRemoteUrl(a, reciterId);
    try {
      const blob = await fetchAyahBlob(remoteUrl, ctrl.signal);
      await audioStorage.saveAudioFile({
        ayahKey: a.ayahKey,
        surahNumber: a.surahNumber,
        ayahNumber: a.ayahNumber,
        globalAyahNumber: a.globalAyahNumber,
        hizbNumber: a.hizbNumber,
        reciterId: reciter.id,
        riwaya: reciter.riwaya,
        remoteUrl,
      }, blob);
      downloaded += 1;
    } catch (err) {
      if (err.name === 'AbortError') {
        setProgress(downloadId, { status: 'cancelled' });
        return { downloaded, failed, cancelled: true };
      }
      failed += 1;
      setProgress(downloadId, {
        error: err?.message
          ? `Téléchargement échoué (${err.message}). Vérifie ta connexion ou change de récitateur.`
          : "Téléchargement échoué. Vérifie ta connexion.",
      });
    }
    setProgress(downloadId, { downloadedFiles: downloaded, failedFiles: failed });
  }
  inflight.delete(downloadId);
  return { downloaded, failed, cancelled: false };
}

async function runScopeDownload({ scope, ayahs, reciterId, riwaya, downloadId }) {
  // Mark scope as downloading
  await audioStorage.upsertDownloadedScope({
    ...scope,
    reciterId,
    riwaya,
    ayahKeys: ayahs.map((a) => a.ayahKey),
    status: 'downloading',
  });

  setProgress(downloadId, {
    downloadId,
    scopeType: scope.scopeType,
    totalFiles: ayahs.length,
    downloadedFiles: 0,
    failedFiles: 0,
    status: 'downloading',
  });

  // Skip already-cached files to support resume.
  const missing = await ensureMissingFiles(ayahs, reciterId, riwaya);
  const alreadyDone = ayahs.length - missing.length;
  setProgress(downloadId, { downloadedFiles: alreadyDone });

  const result = await downloadAyahsToCache(missing, reciterId, riwaya, downloadId);
  if (result.cancelled) {
    await audioStorage.upsertDownloadedScope({
      ...scope, reciterId, riwaya,
      ayahKeys: ayahs.map((a) => a.ayahKey),
      status: 'partial',
    });
    return;
  }

  const status = result.failed > 0
    ? (result.failed === missing.length ? 'failed' : 'partial')
    : 'completed';
  await audioStorage.upsertDownloadedScope({
    ...scope, reciterId, riwaya,
    ayahKeys: ayahs.map((a) => a.ayahKey),
    status,
    downloadedAt: status === 'completed' ? new Date().toISOString() : undefined,
    sizeBytes: ayahs.length * AVERAGE_AYAH_BYTES_128,
  });
  setProgress(downloadId, { status });
}

export const audioDownload = {
  subscribe(downloadId, cb) {
    if (!subscribers.has(downloadId)) subscribers.set(downloadId, new Set());
    subscribers.get(downloadId).add(cb);
    const p = progresses.get(downloadId);
    if (p) cb(p);
    return () => {
      const set = subscribers.get(downloadId);
      if (set) set.delete(cb);
    };
  },

  getProgress(downloadId) {
    return progresses.get(downloadId);
  },

  /** Start downloading a surah. Returns the downloadId. */
  async downloadSurah(surahNumber, reciterId, riwaya) {
    const ayahs = await getAyahsForSurah(surahNumber);
    if (!ayahs.length) throw new Error('Surah introuvable');
    const downloadId = newDownloadId();
    runScopeDownload({
      scope: { scopeType: 'surah', surahNumber },
      ayahs, reciterId, riwaya, downloadId,
    });
    return downloadId;
  },

  async downloadHizb(hizbNumber, reciterId, riwaya) {
    const ayahs = await getAyahsForHizb(hizbNumber);
    if (!ayahs.length) throw new Error('Hizb introuvable');
    const downloadId = newDownloadId();
    runScopeDownload({
      scope: { scopeType: 'hizb', hizbNumber },
      ayahs, reciterId, riwaya, downloadId,
    });
    return downloadId;
  },

  async downloadFullQuran(reciterId, riwaya) {
    const ayahs = await getAyahsForFullQuran();
    const downloadId = newDownloadId();
    runScopeDownload({
      scope: { scopeType: 'full_quran' },
      ayahs, reciterId, riwaya, downloadId,
    });
    return downloadId;
  },

  cancelDownload(downloadId) {
    const ctrl = inflight.get(downloadId);
    if (ctrl) ctrl.abort();
  },

  /** Restart a partial scope: only missing ayahs are re-fetched. */
  async resumeScope(scopeRecord) {
    if (!scopeRecord) throw new Error('No scope to resume');
    const reciterId = scopeRecord.reciterId;
    const riwaya = scopeRecord.riwaya;
    let ayahs = [];
    if (scopeRecord.scopeType === 'surah') ayahs = await getAyahsForSurah(scopeRecord.surahNumber);
    else if (scopeRecord.scopeType === 'hizb') ayahs = await getAyahsForHizb(scopeRecord.hizbNumber);
    else ayahs = await getAyahsForFullQuran();
    const downloadId = newDownloadId();
    runScopeDownload({
      scope: {
        scopeType: scopeRecord.scopeType,
        surahNumber: scopeRecord.surahNumber,
        hizbNumber: scopeRecord.hizbNumber,
      },
      ayahs, reciterId, riwaya, downloadId,
    });
    return downloadId;
  },

  estimateDownloadSizeBytes(ayahCount) {
    return ayahCount * AVERAGE_AYAH_BYTES_128;
  },
};
